import { AudioLines, TriangleAlert } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { useAudio, useConfig } from '@chainlit/react-client';

import AudioPresence from '@/components/AudioPresence';
import { Button } from '@/components/ui/button';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from '@/components/ui/tooltip';

const LISTENING_COLOR = '#1e3a8a';
const ERROR_DISPLAY_MS = 2000;

interface Props {
    disabled?: boolean;
}

export const VoiceBtn = ({ disabled }: Props) => {
    const { config } = useConfig();
    const { startConversation, endConversation, audioConnection } = useAudio();
    const isEnabled = !!config?.features.audio.enabled;
    const [hasError, setHasError] = useState(false);
    const prevConnectionRef = useRef(audioConnection);

    useEffect(() => {
        const wasConnecting = prevConnectionRef.current === 'connecting';
        prevConnectionRef.current = audioConnection;
        if (wasConnecting && audioConnection === 'off') {
            setHasError(true);
            return;
        }
        if (audioConnection !== 'off') setHasError(false);
    }, [audioConnection]);

    useEffect(() => {
        if (!hasError) return;
        const timer = setTimeout(() => setHasError(false), ERROR_DISPLAY_MS);
        return () => clearTimeout(timer);
    }, [hasError]);

    useHotkeys(
        'p',
        () => {
            if (!isEnabled) return;
            if (audioConnection === 'on') return endConversation();
            return startConversation();
        },
        [isEnabled, audioConnection, startConversation, endConversation]
    );

    if (!isEnabled) return null;

    const isEngaged = audioConnection === 'on' || audioConnection === 'connecting';

    return (
        <div id="voice-button">
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        variant={hasError ? 'destructive-solid' : 'tertiary'}
                        size="icon"
                        active={isEngaged}
                        disabled={disabled}
                        style={{
                            width: isEngaged || hasError ? '6.25rem' : '2.25rem',
                            height: '2.25rem'
                        }}
                        className={`
                            transition-[width] duration-300 ease-in-out
                            overflow-hidden
                            ${
                                audioConnection === 'on'
                                    ? '[--active:hsl(217_91%_60%/0.14)] [--border:217_91%_60%_/_0] text-blue-900 dark:text-blue-300'
                                    : ''
                            }
                            ${hasError ? 'animate-shake' : ''}
                        `}
                        onClick={isEngaged ? endConversation : startConversation}
                    >
                        {hasError ? (
                            <span className="flex items-center gap-2.5 leading-none">
                                <TriangleAlert className="size-5" />
                                <span>Error</span>
                            </span>
                        ) : isEngaged ? (
                            <span className="flex items-center gap-2.5 leading-none">
                                {audioConnection === 'on' ? (
                                    <AudioPresence
                                        type="client"
                                        height={18}
                                        width={20}
                                        barCount={6}
                                        barSpacing={1}
                                        color={LISTENING_COLOR}
                                    />
                                ) : (
                                    <AudioLines className="size-5" />
                                )}
                                <span>Stop</span>
                            </span>
                        ) : (
                            <AudioLines />
                        )}
                    </Button>
                </TooltipTrigger>
                <TooltipContent>Voice input (P)</TooltipContent>
            </Tooltip>
        </div>
    );
};
